import * as React from 'react'
import {useSnackbar} from 'notistack'
import {MERCHANT} from '../data/noberoFreeze'
import {int, kg} from '../format'
import {catalogueCounters, combine, rollUp} from './catalogueMath'
import {
  filterSkus, rowSignature, selectionCodes, mergeBlockers, canMerge, plannedImportRows,
} from './catalogueState'

export const PER_PAGE = 12

/**
 * The selection-disclosure derivation (decided after the brief was written).
 * `catalogueState.selectionCodes` deliberately keeps a code selected even
 * when the active filter hides its row — a merge is a considered two-row
 * pick, not a select-all sweep, so a search keystroke must never silently
 * drop it. But a merge rolls up shipment histograms, so acting on a row the
 * merchant can no longer see changes their weight data invisibly. The
 * chosen fix: keep the selection, and say so — the selection bar reads
 * "N selected (M not visible under current filters)" with an affordance to
 * reveal them, and drops the parenthetical entirely once nothing is hidden.
 *
 * Pure and exported on its own so that decision's actual logic — which
 * codes count as "hidden" — is unit-tested without mounting anything (see
 * useCatalogueState.test.js). `visibleRows` is the FILTERED set (every
 * matching row across every page), never just the current page — hiding by
 * pagination is not what this is about, only hiding by a search/source/
 * sub-category filter that excludes the row outright.
 */
export function selectionDisclosure(selection, skus, visibleRows) {
  const selected = selectionCodes(selection, skus)
  const visible = new Set((visibleRows || []).map((r) => r.sku))
  const hidden = selected.filter((code) => !visible.has(code))
  return {selected, hidden}
}

/**
 * Thin UI wrapper around the shared Weight Freeze store (see
 * ../state/useFreezeStore.js), passed in as `store` — the object
 * `useFreezeStore()` returns, threaded down from index.js. This hook adds
 * NO transition logic of its own: every merge/import/OMS-pull/review/
 * mark-reviewed/fix-code below just calls the matching `store.actions.*`
 * function, which the store's own reducer applies via catalogueState.js's
 * pure functions. All this hook owns is filter state, selection state,
 * pagination, the derived counters/rows/roll views, and the snackbar copy
 * that goes with each action.
 *
 * Deliberately NOT a second store: `store.skus`/`store.mapLog` always come
 * from the ONE useFreezeStore() instance held above the section switch in
 * index.js, so a merge made here is still there after the merchant clicks
 * to another rail item and back. Do not add a local `useState` here for
 * anything that Packing or Analytics also need to read.
 */
export default function useCatalogueState(store) {
  const {enqueueSnackbar} = useSnackbar()
  const {skus, mapLog, packs, catalogueSyncing, actor, actions: storeActions} = store

  const [filters, setFilters] = React.useState({q: '', source: 'all', sub: 'all'})
  const [selection, setSelection] = React.useState(() => new Set())
  const [page, setPage] = React.useState(1)

  const roll = React.useMemo(() => rollUp(skus), [skus])

  // Page resets when the row SET changes, never when a weight does — see
  // rowSignature's own doc comment.
  const signature = React.useMemo(() => rowSignature(skus), [skus])
  React.useEffect(() => { setPage(1) }, [signature, filters.q, filters.source, filters.sub])

  const rows = React.useMemo(() => filterSkus(skus, filters), [skus, filters])

  const setFilter = React.useCallback((key, value) => {
    setFilters((f) => ({...f, [key]: value}))
  }, [])

  const resetFilters = React.useCallback(() => setFilters({q: '', source: 'all', sub: 'all'}), [])

  const toggleSelect = React.useCallback((code) => {
    setSelection((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }, [])

  const selectAllOnPage = React.useCallback((codes, on) => {
    setSelection((prev) => {
      const next = new Set(prev)
      codes.forEach((c) => (on ? next.add(c) : next.delete(c)))
      return next
    })
  }, [])

  const clearSelection = React.useCallback(() => setSelection(new Set()), [])

  const disclosure = React.useMemo(() => selectionDisclosure(selection, skus, rows), [selection, skus, rows])
  const selected = disclosure.selected
  const hiddenSelected = disclosure.hidden
  const blocked = mergeBlockers(selection, skus)

  // `shipped` is the only figure here that is not derived from the list —
  // it is what the account has actually shipped, catalogued or not — so it is
  // added on top of the shared derivation rather than pushed into it.
  const counters = React.useMemo(() => ({
    ...catalogueCounters(skus),
    shipped: MERCHANT.totals.skus,
  }), [skus])

  const subOptions = React.useMemo(
    () => Array.from(new Set(skus.map((s) => s.sub))).sort(),
    [skus],
  )

  // Falling edge of the store's OMS-pull flag. The pull itself is scheduled
  // and applied inside useFreezeStore (see its own header) so the data
  // lands even if the merchant has already navigated off Catalogue — this
  // effect only decides whether THIS mount is around to show the toast for
  // it. No toast fires for a completion nobody was here to see start.
  const prevSyncingRef = React.useRef(catalogueSyncing)
  React.useEffect(() => {
    if (prevSyncingRef.current && !catalogueSyncing) {
      enqueueSnackbar('OMS/WMS sync finished — 3 SKUs pulled in with their warehouse weights.', {variant: 'success'})
    }
    prevSyncingRef.current = catalogueSyncing
  }, [catalogueSyncing, enqueueSnackbar])

  const actions = {
    merge(mother, codes) {
      const before = skus.filter((s) => codes.indexOf(s.sku) >= 0 || s.sku === mother)
      const rolled = combine(before)
      storeActions.mergeSkus(mother, codes)
      setSelection(new Set())
      enqueueSnackbar(
        `Merged under ${mother} — ${int(rolled.ships)} shipments now roll up, most probable weight ${kg(rolled.mpw)}.`,
        {variant: 'success'},
      )
    },
    undoMerge(entry) {
      storeActions.undoMergeSku(entry.id)
      enqueueSnackbar(`Merge reversed — ${entry.children.length} SKUs are standalone again.`, {variant: 'info'})
    },
    importRows(importedRows, fileName) {
      storeActions.importCatalogue(importedRows)
      enqueueSnackbar(
        `Catalogue imported — ${importedRows.added.length} added and ${importedRows.updated.length} updated from ${fileName}.`,
        {variant: 'success'},
      )
    },
    pullFromOms() {
      if (catalogueSyncing) return
      storeActions.pullFromOms()
    },
    saveReview(code, patch) {
      storeActions.reviewSku(code, patch)
      enqueueSnackbar(`${code} is reviewed and ready to take into a freeze request.`, {variant: 'success'})
    },
    markReviewed(codes) {
      storeActions.markSkusReviewed(codes)
      setSelection(new Set())
      enqueueSnackbar(`${codes.length} SKUs marked reviewed.`, {variant: 'success'})
    },
    saveCode(code, nextCode) {
      storeActions.fixSkuCode(code, nextCode)
      setSelection(new Set())
      enqueueSnackbar(`Product code saved — ${code} is now ${nextCode}.`, {variant: 'success'})
    },
  }

  return {
    state: {skus, mapLog}, rows, roll, packs, merchant: MERCHANT, actor,
    filters, setFilter, resetFilters,
    selection, selected, hiddenSelected, blocked, canMerge: canMerge(selection, skus),
    toggleSelect, selectAllOnPage, clearSelection,
    page, setPage, perPage: PER_PAGE,
    counters, subOptions, syncing: catalogueSyncing, actions,
    plannedRows: () => plannedImportRows(skus, packs),
  }
}
